/* Team K9 Support hub (/pages/support): instant search with inline answers, quick paths into the
   embedded Order Help portal (plus deep links), Fit Studio and the guide/video lightbox.
   Sections: tk9-support-hub-top / -bottom. Portal: tk9-order-help (embedded).
   Chat helper: tk9-support-chat.js, which talks to this file through window.TK9SupportHub (end of file).
   Deep links:  ?intent=swap|return|claim|track|change|cancel|address  (or #swap, #track …)
                #fit  #fit-classic  #contact  #answers  #faq-returns  ?q=refund  ?fit=collar */
(() => {
  const top = document.querySelector("[data-tk9sh]");
  const bottom = document.querySelector("[data-tk9sh-bottom]");
  if (!top || top.dataset.ready) return;
  top.dataset.ready = "1";
  // Pattern, box dog and closing scene are decoration below the first screen: fetch them after the page has loaded.
  const artOn = () => setTimeout(() => document.documentElement.classList.add("tk9sh-art"), 250);
  if (document.readyState === "complete") artOn(); else window.addEventListener("load", artOn, { once: true });

  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));
  const esc = (value) => String(value == null ? "" : value).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const scrollToEl = (el) => el && el.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
  const isTyping = (el) => el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable);
  const ICON_X = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>';
  const ICON_CHEVRON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';

  // ------------------------------------------------------------------ Merchanting Watch
  // Same contract as the portal: enumerated component ids, queued until the recorder loads.
  // Clicks on declared [data-watch-component] elements are counted by the recorder itself.
  const watchQueue = [];
  let watchTimer = null;
  function watchFlush() {
    const recorder = window.MerchantingWatch;
    if (!recorder || typeof recorder.track !== "function") return false;
    while (watchQueue.length) {
      const details = watchQueue.shift();
      try { recorder.track("element_action", details); } catch {}
    }
    return true;
  }
  function watch(kind, action, id) {
    watchQueue.push({ kind, action, component_id: String(id).toLowerCase().replace(/[^a-z0-9_.:-]/g, "_").slice(0, 100) });
    if (watchQueue.length > 60) watchQueue.shift();
    if (watchFlush() || watchTimer) return;
    let tries = 0;
    watchTimer = setInterval(() => {
      tries += 1;
      if (watchFlush() || tries > 40) { clearInterval(watchTimer); watchTimer = null; }
    }, 1500);
  }

  // ------------------------------------------------------------------ analytics
  // The same moments (a search, an answer read, a hand-off to Order Help …) go to three places:
  //  1. Anonymous counts → the Order Help Worker → the team dashboard's "Support hub" card and the daily Slack
  //     digest. No cookies, no storage, no visitor ids; batched, sent with sendBeacon. The vocabulary is
  //     HUB_EVENTS in workers/teamk9-order-help-worker.js: public content ids and short enumerations only.
  //  2. Merchanting Watch, through watch() above (a small fixed set of ids: the recorder keeps 60 per page view).
  //  3. window.dataLayer, only if the site already has one: { event: "tk9_support_hub", hub_event, hub_value }.
  // Nothing is counted when the browser sends Do Not Track / Global Privacy Control, when the visitor declined
  // analytics (Shopify privacy API), in the theme editor, on localhost, in the portal's demo mode, or when the
  // section setting "Send anonymous usage counts" is off. Words from a search that found nothing leave the page
  // only when analytics are accepted, and only as plain words (letters only, no @, under 60 characters).
  const portalRoot = $("[data-tk9oh]");
  const API = ((portalRoot && portalRoot.dataset.api) || "").trim();
  const PAGE_VERSION = (top.dataset.watchPageVersion || "").slice(0, 40);
  const DEV_HOST = /^(localhost|127\.0\.0\.1|\[::1\]|.+\.localhost)$/.test(location.hostname) && !/[?&]tk9sh_analytics=1(&|$)/.test(location.search);
  const slug = (value, max = 40) => String(value == null ? "" : value).toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "").replace(/['’]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, max).replace(/-+$/, "");
  function consent() {
    try {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      return privacy && typeof privacy.analyticsProcessingAllowed === "function" ? privacy.analyticsProcessingAllowed() : null;
    } catch { return null; }
  }
  const optedOut = () => navigator.globalPrivacyControl === true || navigator.doNotTrack === "1" || window.doNotTrack === "1";
  // the portal's own demo switch (?demo=1 or its setting): sample orders, so sample traffic
  const DEMO = new URLSearchParams(location.search).get("demo") === "1" || Boolean(portalRoot && portalRoot.dataset.demo === "true");
  const reporting = () => /^https:\/\//.test(API) && top.dataset.analytics !== "false" && !DEV_HOST
    && !DEMO && !(window.Shopify && window.Shopify.designMode) && !optedOut() && consent() !== false;

  const outbox = [];
  const counted = new Set();
  let reported = 0;
  let flushTimer = null;
  const deviceClass = () => (window.innerWidth < 700 ? "m" : window.innerWidth < 1025 ? "t" : "d");
  function post(payload) {
    const body = JSON.stringify(payload);
    try { if (navigator.sendBeacon && navigator.sendBeacon(API, new Blob([body], { type: "text/plain" }))) return; } catch {}
    try { fetch(API, { method: "POST", body, headers: { "content-type": "text/plain" }, keepalive: true, credentials: "omit", mode: "cors" }).catch(() => {}); } catch {}
  }
  function flush() {
    clearTimeout(flushTimer);
    flushTimer = null;
    const pending = outbox.splice(0);
    if (!pending.length || !reporting()) return;
    const accepted = consent() === true ? 1 : 0;
    const events = pending.filter((e) => accepted || e.k !== "search_none_q");
    for (let i = 0; i < events.length; i += 25) post({ action: "track", event: "hub", d: deviceClass(), p: PAGE_VERSION, c: accepted, events: events.slice(i, i + 25) });
  }
  // once: count a (key, value) pair at most once per page view (opening the same answer twice is one read).
  function track(key, value, { once = false } = {}) {
    const v = String(value == null ? "" : value).toLowerCase();
    if (once) {
      const id = `${key}|${v}`;
      if (counted.has(id)) return false;
      counted.add(id);
    }
    if (reported >= 150 || !reporting()) return false;
    reported += 1;
    outbox.push({ k: key, v });
    // third-party tags (GA/GTM) get ids and enumerations only, never the search words
    try { if (key !== "search_none_q" && Array.isArray(window.dataLayer)) window.dataLayer.push({ event: "tk9_support_hub", hub_event: key, hub_value: v }); } catch {}
    if (outbox.length >= 20) flush();
    else if (!flushTimer) flushTimer = setTimeout(flush, 8000);
    return true;
  }
  // One moment → the dashboard count and (optionally) a Watch element action.
  function signal(key, value, watchArgs, options) {
    track(key, value, options);
    if (watchArgs) watch(watchArgs[0], watchArgs[1], watchArgs[2]);
  }

  // Page health, reported once when the visitor leaves or switches away: rating only, never the number. Measured on a
  // quarter of visits (a sample is plenty for a percentage and keeps the writes down).
  const VITALS_SAMPLE = Math.random() < 0.25;
  const vitals = { lcp: null, cls: null, inp: null };
  const observeVital = (type, extra, onEntries) => {
    try { new PerformanceObserver((list) => onEntries(list.getEntries())).observe({ type, buffered: true, ...extra }); } catch {}
  };
  observeVital("largest-contentful-paint", {}, (entries) => { const last = entries[entries.length - 1]; if (last) vitals.lcp = last.startTime; });
  // a page with no layout shifts never calls the observer back, so start the total at 0 where it is supported
  try { if (PerformanceObserver.supportedEntryTypes.indexOf("layout-shift") > -1) vitals.cls = 0; } catch {}
  observeVital("layout-shift", {}, (entries) => entries.forEach((e) => { if (!e.hadRecentInput) vitals.cls = (vitals.cls || 0) + e.value; }));
  observeVital("event", { durationThreshold: 40 }, (entries) => entries.forEach((e) => { if (e.interactionId) vitals.inp = Math.max(vitals.inp || 0, e.duration); }));
  let vitalsSent = false;
  function reportVitals() {
    if (vitalsSent) return;
    vitalsSent = true;
    if (!VITALS_SAMPLE) return;
    const rate = (value, good, poor) => (value <= good ? "good" : value <= poor ? "ni" : "poor");
    if (vitals.lcp != null) track("vitals", `lcp:${rate(vitals.lcp, 2500, 4000)}`);
    if (vitals.cls != null) track("vitals", `cls:${rate(vitals.cls, 0.1, 0.25)}`);
    if (vitals.inp != null) track("vitals", `inp:${rate(vitals.inp, 200, 500)}`);
  }
  // Only this script's own failures count (the page has plenty of other scripts).
  window.addEventListener("error", (event) => {
    try { if (/tk9-support-hub/.test(event.filename || "")) track("error", `js:${slug((event.error && event.error.name) || "error", 24) || "error"}`, { once: true }); } catch {}
  });
  window.addEventListener("unhandledrejection", (event) => {
    try { if (event.reason && /tk9-support-hub/.test(String(event.reason.stack || ""))) track("error", "promise", { once: true }); } catch {}
  });

  // ------------------------------------------------------------------ paths → Order Help
  const portal = () => document.getElementById("order-help");
  const FLOW_ALIASES = {
    swap: "swap", exchange: "swap", resize: "swap",
    return: "return", refund: "return",
    claim: "claim", problem: "claim", damaged: "claim",
    track: "track", tracking: "track",
    change: "change", edit: "change",
    cancel: "cancel", address: "address"
  };
  function openIntent(flow) {
    const target = portal();
    if (!target) { window.location.href = "/pages/order-help"; return; }
    scrollToEl(target);
    // "change" just opens the order (cancel / address tiles appear when the order allows them).
    if (flow && flow !== "change") document.dispatchEvent(new CustomEvent("tk9oh:intent", { detail: { flow } }));
    setTimeout(() => {
      const input = target.querySelector('input[name="order"]');
      if (input && window.matchMedia("(min-width: 700px)").matches) input.focus({ preventScroll: true });
    }, reduceMotion ? 0 : 450);
  }
  function jump(id) {
    const el = document.getElementById(id);
    if (el) scrollToEl(el);
  }
  // Where a hand-off to Order Help started: the quick-path tiles, an FAQ answer, the inline answer card, Fit Studio, the
  // contact block, a search result ("search" is set while choose() clicks it) or a link with ?intent= ("deeplink").
  let intentSource = "";
  const sourceOf = (el) => intentSource || (el.closest(".tk9sc, .tk9sc__panel") ? "chat" : el.closest(".tk9sh__paths") ? "tile" : el.closest("[data-tk9sh-answer]") ? "answer" : el.closest(".tk9sh__faq") ? "faq" : el.closest(".tk9sh__finder") ? "fit" : el.closest(".tk9sh__contact") ? "contact" : "tile");
  const handOff = (flow, source) => signal("intent", `${flow}.${source}`, ["cta", "open", `hub.intent.${flow}`]);
  document.addEventListener("click", (event) => {
    const intent = event.target.closest("[data-tk9sh-intent]");
    if (intent) {
      event.preventDefault();
      const flow = intent.dataset.tk9shIntent;
      const source = sourceOf(intent);
      if (source === "tile") track("path_click", flow);
      handOff(flow, source);
      openIntent(flow);
      return;
    }
    const jumper = event.target.closest("[data-tk9sh-jump]");
    if (jumper) {
      event.preventDefault();
      if (jumper.closest(".tk9sh__paths") && !intentSource) track("path_click", jumper.dataset.tk9shJump);
      jump(jumper.dataset.tk9shJump);
    }
  });
  // Guides, policies, mail and phone links inside the hub (anything that leaves the hub or opens a mail/phone app).
  const linkId = (href) => {
    if (/^mailto:/i.test(href)) return "mailto";
    if (/^tel:/i.test(href)) return "tel";
    try {
      const url = new URL(href, location.href);
      return slug(url.origin === location.origin ? url.pathname.split("/").filter(Boolean).pop() || "home" : url.hostname.replace(/^www\./, "")) || "link";
    } catch { return "link"; }
  };
  document.addEventListener("click", (event) => {
    const link = event.target.closest("a[href]");
    if (!link || !(top.contains(link) || (bottom && bottom.contains(link))) || link.closest("[data-tk9sh-intent], [data-tk9sh-jump], [data-tk9sh-reveal]")) return;
    const href = link.getAttribute("href") || "";
    if (href.charAt(0) !== "#") track("link_click", linkId(href));
  }, true);

  // ------------------------------------------------------------------ Fit studio
  const CDN = "https://cdn.shopify.com/s/files/1/2715/4364/files/";
  const video = (id, title, sub) => ({ type: "video", id, title, sub, thumb: `https://i.ytimg.com/vi/${id}/hqdefault.jpg` });
  const guide = (src, title, sub) => ({ type: "guide", src, title, sub, thumb: `${src}${src.includes("?") ? "&" : "?"}width=400` });
  // Videos are for the Tactical and Classic harnesses only (owner, 2026-09-13).
  const TIGHTEN = video("4dzHaAIMyPA", "How to tighten the straps", "Video · max tightening");
  const ADJUST = video("EM7vfINEd8Q", "Adjust for a perfect fit", "Video · front straps");
  const BETWEEN = "Between sizes? Size down. A puppy that is still filling out should get the larger size.";
  // Charts mirror FIT in assets/tk9-order-help.js (support agent sizeExchangePolicy.ts).
  const LINES = {
    tactical: {
      name: "Tactical Harness",
      blurb: "Our no-pull harness with four quick-release metal buckles.",
      cols: [["lb", "Weight", "lb"], ["chest", "Chest", "in"], ["mid", "Midsection", "in"]],
      chart: [
        { key: "XS", label: "Extra-Small", lb: [15, 25], chest: [14, 22], mid: [18, 26] },
        { key: "S", label: "Small", lb: [25, 48], chest: [18, 29], mid: [24, 33] },
        { key: "M", label: "Medium", lb: [48, 78], chest: [26, 34], mid: [30, 40] },
        { key: "L", label: "Large", lb: [78, 120], chest: [30, 42], mid: [34, 45] },
        { key: "XL", label: "Extra-Large", lb: [120, 170], chest: [29, 44], mid: [37, 48], plus: true }
      ],
      inputs: [["lb", "Weight", "lb"], ["mid", "Midsection", "in", "optional"]],
      tip: BETWEEN,
      media: [TIGHTEN, ADJUST, guide(`${CDN}New_Harness_Guide_V3__1.23.26_ded5d370-c7b4-47c1-a8df-a35e70c48e50.jpg?v=1774291683`, "Tactical size guide", "Chest + midsection")]
    },
    classic: {
      name: "Classic Harness",
      blurb: "Our harness with two metal buckles.",
      cols: [["lb", "Weight", "lb"], ["chest", "Chest", "in"], ["mid", "Belly", "in"]],
      chart: [
        { key: "S", label: "Small", lb: [20, 45], chest: [16, 27], mid: [24, 33] },
        { key: "M", label: "Medium", lb: [45, 75], chest: [24, 31], mid: [30, 39] },
        { key: "L", label: "Large", lb: [75, 115], chest: [27, 41], mid: [34, 45] },
        { key: "XL", label: "Extra-Large", lb: [115, 170], chest: [29, 44], mid: [37, 48], plus: true }
      ],
      inputs: [["lb", "Weight", "lb"], ["mid", "Belly", "in", "optional"]],
      tip: BETWEEN,
      floor: "Small is the smallest Classic. For dogs under 20 lb, the Tactical comes in Extra-Small (15–25 lb).",
      media: [TIGHTEN, ADJUST, guide(`${CDN}sizeguidesquare_0d900098-3753-4982-bb8f-c24b10a5cb9d.png?v=1`, "Classic size guide", "Chest + belly")]
    },
    lightweight: {
      name: "Lightweight Harness",
      blurb: "A strap harness with one buckle and its own size chart.",
      steps: [
        "Slip it over your dog's head and ears.",
        "Buckle the stomach strap just behind the front legs.",
        "Tighten the stomach strap until two flat fingers fit snugly under it. It is the only strap that adjusts; the chest strap is a fixed length.",
        "For a dog that pulls, clip the leash to the front V-ring on the chest strap."
      ],
      cols: [["lb", "Weight", "lb"], ["mid", "Midsection", "in"]],
      chart: [
        { key: "S", label: "Small", lb: [25, 40], mid: [21.5, 27] },
        { key: "M", label: "Medium", lb: [40, 55], mid: [24.5, 30] },
        { key: "L", label: "Large", lb: [55, 80], mid: [27.5, 33] },
        { key: "XL", label: "Extra-Large", lb: [80, 100], mid: [29, 35] }
      ],
      inputs: [["lb", "Weight", "lb"], ["mid", "Midsection", "in", "optional"]],
      tip: "Measure the midsection just behind the front legs. Between sizes? Size down.",
      media: [
        guide(`${CDN}04-fine-tune-their-fit.png?v=1789089472`, "Fine-tune the fit", "Strap adjustment"),
        guide(`${CDN}lightweight-harness-size-guide-v3_922224b5-5abc-4acd-a1ff-0a7daf9d439b.jpg?v=1789137694`, "Lightweight size guide", "Weight + midsection")
      ]
    },
    collar: {
      name: "Tactical Collar",
      blurb: "Sized by neck. Measure where the collar sits.",
      cols: [["neck", "Neck", "in"]],
      chart: [
        { key: "M", label: "Medium", neck: [13, 17] },
        { key: "L", label: "Large", neck: [17, 20] },
        { key: "XL", label: "Extra-Large", neck: [20, 24] }
      ],
      inputs: [["neck", "Neck", "in"]],
      tip: "Two fingers should fit snugly under the collar. Medium is our smallest collar.",
      media: []
    }
  };
  const SEAT = {
    media: [
      guide(`${CDN}hard_bottom_csc_2.png?v=1723136608`, "Large: cars & SUVs", "Measurement diagram"),
      guide(`${CDN}hard_bottom_csc_1.png?v=1723136609`, "Extra-Large: trucks", "Measurement diagram"),
      guide(`${CDN}upd_2048x2048_213ea08f-0126-4251-99ca-c329a3232c5f.webp?v=1718239978`, "Vehicle compatibility", "Check before ordering")
    ]
  };

  // Ranges as the live products list them ("Small (25-48 LB)", "Medium (13-17 in)"), read by the section from each
  // product's Size option, the same source as the Size Guide page. Applied to a line only when every size matches
  // by name, the unit is the one that line is sized by, and every range is sane; otherwise the chart above stands.
  (function applyLiveSizes() {
    let live = null;
    try { live = JSON.parse(($("[data-fit-live]") || {}).textContent || "null"); } catch (error) { live = null; }
    if (!live) return;
    Object.keys(LINES).forEach((key) => {
      const names = live[key];
      const chart = LINES[key].chart;
      if (!Array.isArray(names) || !names.length) return;
      const field = chart[0].lb ? "lb" : chart[0].neck ? "neck" : "";
      const unit = field === "lb" ? "lb" : "in";
      if (!field) return;
      const parsed = names.length !== chart.length ? [null] : names.map((name) => {
        const match = /^\s*([^()]+?)\s*\(\s*(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)(\+?)\s*(lb|in)\b/i.exec(String(name));
        return match && match[5].toLowerCase() === unit ? { label: match[1].trim().toLowerCase(), lo: Number(match[2]), hi: Number(match[3]), plus: Boolean(match[4]) } : null;
      });
      const usable = !parsed.some((row) => !row || !(row.lo < row.hi))
        && chart.every((row, i) => parsed[i].label === row.label.toLowerCase())
        && parsed.every((row, i) => i === 0 || row.lo >= parsed[i - 1].lo);
      if (!usable) {
        // The product's sizes changed in a way this chart does not know: say so for whoever is checking the page.
        if (window.console && console.warn) console.warn(`[Team K9 support hub] The live size names for "${key}" do not match the size finder's chart; showing the built-in chart. Update LINES in tk9-support-hub.js (and FIT in tk9-order-help.js).`);
        return;
      }
      chart.forEach((row, i) => { row[field] = [parsed[i].lo, parsed[i].hi]; if (field === "lb") row.plus = parsed[i].plus; });
      LINES[key].liveSizes = true;
    });
  })();

  const range = (r, plus) => (r ? `${r[0]}–${r[1]}${plus ? "+" : ""}` : "n/a");
  // The weight slider sits over a band of the sizes drawn to scale, so the thumb is always above the size it means.
  function scaleOf(line) {
    const rows = line.chart.filter((row) => row.lb);
    if (!rows.length || !line.inputs.some(([key]) => key === "lb")) return null;
    const first = rows[0].lb[0];
    const last = rows[rows.length - 1];
    const min = Math.max(1, first - 5);
    const max = last.lb[1] + (last.plus ? 10 : 5);
    return { rows, min, max };
  }
  function scaleHtml(line, lb, rec) {
    const scale = scaleOf(line);
    if (!scale) return "";
    const { rows, min, max } = scale;
    const span = max - min;
    const last = rows[rows.length - 1];
    const bands = rows.map((row, i) => {
      const from = i === 0 ? min : row.lb[0];
      const to = i === rows.length - 1 ? max : row.lb[1];
      return `<span class="tk9sh__band${rec && rec.row.key === row.key ? " is-match" : ""}" data-fit-band="${esc(row.key)}" style="flex: ${((to - from) / span).toFixed(4)} 1 0"><b>${esc(row.key)}</b></span>`;
    }).join("");
    const value = lb > 0 ? Math.min(max, Math.max(min, Math.round(lb))) : min;
    return `<div class="tk9sh__scale${lb > 0 ? " is-set" : ""}" data-fit-scale>
          <label class="tk9sh__scale-label" for="tk9sh-fit-slider">Slide to your dog's weight</label>
          <input type="range" class="tk9sh__slider" id="tk9sh-fit-slider" min="${min}" max="${max}" step="1" value="${value}" data-fit-slider aria-valuetext="${lb > 0 ? `${value} pounds${rec ? `, ${esc(rec.row.label)}` : ""}` : "Not set"}">
          <div class="tk9sh__bands" aria-hidden="true">${bands}</div>
          <div class="tk9sh__scale-ends" aria-hidden="true"><span>${rows[0].lb[0]} lb</span><span>${last.lb[1]}${last.plus ? "+" : ""} lb</span></div>
        </div>`;
  }
  function recommend(line, values) {
    const chart = line.chart;
    const pick = (key, value) => {
      if (!(value > 0)) return null;
      const hits = chart.filter((row) => row[key] && value >= row[key][0] && value <= row[key][1]);
      if (hits.length) return { row: hits[0], between: hits.length > 1 };
      const first = chart.find((row) => row[key]);
      const last = [...chart].reverse().find((row) => row[key]);
      if (value < first[key][0]) return { row: first, below: true };
      if (value > last[key][1]) return { row: last, above: true };
      // in a gap between published ranges: the smaller size
      const smaller = [...chart].reverse().find((row) => row[key] && value > row[key][1]);
      return { row: smaller || first, between: true };
    };
    const byMeasure = line.inputs.filter(([key]) => key !== "lb").map(([key]) => pick(key, values[key])).find(Boolean);
    const byWeight = pick("lb", values.lb);
    // Measurements decide when they're given (chest and belly matter most for fit).
    const result = byMeasure || byWeight;
    if (!result) return null;
    let note = "";
    if (result.below) note = line.floor || `${result.row.label} is our smallest size.`;
    else if (result.above) note = `${result.row.label} is our largest size${result.row.plus ? " (fits 170+ lb)" : ""}.`;
    else if (result.between) note = "Right between sizes. We recommend the smaller one.";
    if (byMeasure && byWeight && byMeasure.row.key !== byWeight.row.key) note = `Going by the measurement. Weight alone suggests ${byWeight.row.label}.`;
    return { ...result, note };
  }

  let currentLine = "tactical";
  const fitValues = {};
  function mediaCard(item, index) {
    const play = item.type === "video" ? `<span class="tk9sh__play"><span><svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg></span></span>` : "";
    return `<button type="button" class="tk9sh__card" data-media="${index}">
      <span class="tk9sh__card-thumb"><img src="${esc(item.thumb)}" alt="" loading="lazy" width="112" height="70">${play}</span>
      <span><b>${esc(item.title)}</b><small>${esc(item.sub)}</small></span>
    </button>`;
  }
  const recHtml = (line, rec) => (rec
    ? `<span class="tk9sh__rec-size">${esc(rec.row.key)}</span><span><b>Start with ${esc(rec.row.label)}</b>${rec.note ? esc(rec.note) : "Confirm with the chart below."}</span>`
    : `<span>Enter ${line.inputs[0][0] === "neck" ? "a neck size" : "your dog's weight"} for a starting size.</span>`);
  // A storybook scene under the guides (decoration: no caption, hidden from assistive tech).
  const plateHtml = () => {
    const src = ($("[data-fit-panel]") || { dataset: {} }).dataset.plate;
    return src ? `<img class="tk9sh__plate" src="${esc(src)}" alt="" width="1400" height="702" loading="lazy" decoding="async" aria-hidden="true">` : "";
  };
  function renderFit() {
    const panel = $("[data-fit-panel]");
    if (!panel) return;
    panel.setAttribute("aria-labelledby", `tk9sh-tab-${currentLine}`);
    if (currentLine === "seat") {
      panel.innerHTML = `
        <div class="tk9sh__finder">
          <h3>Car & truck seat covers</h3>
          <p class="tk9sh__muted">Two sizes, built around your back seat.</p>
          <div class="tk9sh__chart-wrap"><table class="tk9sh__chart"><caption class="tk9sh__sr">Seat cover sizes</caption><thead><tr><th scope="col">Size</th><th scope="col">Best for</th></tr></thead><tbody>
            <tr><th scope="row">Large</th><td>Most cars and SUVs</td></tr>
            <tr><th scope="row">Extra-Large</th><td>Most trucks, such as a Tacoma Double Cab</td></tr>
          </tbody></table></div>
          <div class="tk9sh__tip"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 8h.01M11 12h1v4h1"/></svg><span>Check your back seat against the diagrams before ordering. The vehicle name alone can mislead.</span></div>
          <p class="tk9sh__note-line">Setting it up? <a href="/pages/how-to-install-the-car-seat-cover">How to install the car seat cover</a></p>
        </div>
        <div class="tk9sh__media"><h3>Diagrams</h3><p class="tk9sh__muted">Tap to enlarge.</p><div class="tk9sh__cards">${SEAT.media.map(mediaCard).join("")}</div>${plateHtml()}</div>`;
      return;
    }
    const line = LINES[currentLine];
    const rec = recommend(line, fitValues[currentLine] || {});
    const values = fitValues[currentLine] || {};
    panel.innerHTML = `
      <div class="tk9sh__finder">
        <h3>${esc(line.name)}</h3>
        <p class="tk9sh__muted">${esc(line.blurb)}</p>
        ${scaleHtml(line, values.lb, rec)}
        <div class="tk9sh__inputs">
          ${line.inputs.map(([key, label, unit, optional]) => `<label><span>${esc(label)}${optional ? " <small>optional</small>" : ""}</span>
            <span class="tk9sh__unit"><input type="number" inputmode="decimal" min="0" step="0.5" data-fit-input="${key}" value="${values[key] || ""}" aria-label="${esc(label)} in ${unit === "lb" ? "pounds" : "inches"}"><i aria-hidden="true">${unit}</i></span></label>`).join("")}
        </div>
        <div class="tk9sh__rec ${rec ? "is-on" : ""}" aria-live="polite" data-fit-rec>${recHtml(line, rec)}</div>
        <div class="tk9sh__chart-wrap"><table class="tk9sh__chart"><caption class="tk9sh__sr">${esc(line.name)} size chart</caption><thead><tr><th scope="col">Size</th>${line.cols.map(([, label, unit]) => `<th scope="col">${esc(label)} <span>${unit === "lb" ? "lb" : "in"}</span></th>`).join("")}</tr></thead>
          <tbody>${line.chart.map((row) => `<tr class="${rec && rec.row.key === row.key ? "is-match" : ""}"><th scope="row">${esc(row.key)}</th>${line.cols.map(([key]) => `<td>${esc(range(row[key], key === "lb" && row.plus))}</td>`).join("")}</tr>`).join("")}</tbody>
        </table></div>
        <div class="tk9sh__tip"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 8h.01M11 12h1v4h1"/></svg><span>${esc(line.tip)}</span></div>
        ${line.steps ? `<div class="tk9sh__steps"><b>How it goes on</b><ol>${line.steps.map((step) => `<li>${esc(step)}</li>`).join("")}</ol></div>` : ""}
        ${currentLine === "collar" ? "" : `<p class="tk9sh__note-line">Already have it and it doesn't fit? <a href="#order-help" data-tk9sh-intent="swap">Swap the size</a>. Harness size swaps are free within 60 days of delivery.</p>`}
      </div>
      <div class="tk9sh__media">
        <h3>${line.media.some((m) => m.type === "video") ? "Videos & guides" : "Guides"}</h3>
        <p class="tk9sh__muted">${line.media.length ? "Tap to open." : "Measure the neck where the collar sits, then add two fingers of room."}</p>
        <div class="tk9sh__cards">${line.media.map(mediaCard).join("")}</div>
        ${currentLine === "tactical" || currentLine === "classic" ? `<p class="tk9sh__note-line">More help: <a href="/pages/harness-guide">Harness Guide</a> · <a href="/pages/measurement">Size Guide</a></p>` : ""}
        ${plateHtml()}
      </div>`;
  }
  function selectLine(line, { focus = false, scroll = false } = {}) {
    if (!line || (!LINES[line] && line !== "seat")) return;
    const previous = currentLine;
    currentLine = line;
    // Carry the dog's weight across harness tabs so switching products answers right away.
    const carried = (fitValues[previous] || {}).lb;
    if (carried && LINES[line] && LINES[line].inputs.some(([key]) => key === "lb") && !(fitValues[line] || {}).lb) {
      fitValues[line] = { ...(fitValues[line] || {}), lb: carried };
    }
    $$("[data-fit-tab]").forEach((tab) => {
      const on = tab.dataset.fitTab === line;
      tab.setAttribute("aria-selected", on ? "true" : "false");
      tab.tabIndex = on ? 0 : -1;
      if (on && focus) tab.focus();
    });
    renderFit();
    signal("fit_tab", line, ["size_guide", "open", `hub.fit.${line}`], { once: true });
    if (scroll) jump("fit");
  }
  if (bottom) {
    renderFit();
    bottom.addEventListener("click", (event) => {
      const tab = event.target.closest("[data-fit-tab]");
      if (tab) return selectLine(tab.dataset.fitTab);
      const card = event.target.closest("[data-media]");
      if (card) {
        const list = currentLine === "seat" ? SEAT.media : LINES[currentLine].media;
        const item = list[Number(card.dataset.media)];
        if (item) openLightbox(item, card);
      }
    });
    bottom.addEventListener("keydown", (event) => {
      const tab = event.target.closest("[data-fit-tab]");
      if (!tab || !["ArrowRight", "ArrowLeft", "Home", "End"].includes(event.key)) return;
      event.preventDefault();
      const tabs = $$("[data-fit-tab]");
      const index = tabs.indexOf(tab);
      const next = event.key === "Home" ? 0 : event.key === "End" ? tabs.length - 1 : (index + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
      selectLine(tabs[next].dataset.fitTab, { focus: true });
    });
    let fitDebounce = null;
    bottom.addEventListener("input", (event) => {
      const slider = event.target.closest("[data-fit-slider]");
      const input = slider ? null : event.target.closest("[data-fit-input]");
      if (!slider && !input) return;
      if (slider) {
        fitValues[currentLine] = { ...(fitValues[currentLine] || {}), lb: parseFloat(slider.value) || "" };
        const field = $('[data-fit-input="lb"]');
        if (field) field.value = slider.value;
      } else {
        fitValues[currentLine] = { ...(fitValues[currentLine] || {}), [input.dataset.fitInput]: parseFloat(input.value) || "" };
      }
      clearTimeout(fitDebounce);
      fitDebounce = setTimeout(() => {
        const line = LINES[currentLine];
        const rec = recommend(line, fitValues[currentLine]);
        const box = $("[data-fit-rec]");
        if (!box) return;
        box.classList.toggle("is-on", Boolean(rec));
        box.innerHTML = recHtml(line, rec);
        $$(".tk9sh__chart tbody tr").forEach((tr, i) => tr.classList.toggle("is-match", Boolean(rec) && line.chart[i] && line.chart[i].key === rec.row.key));
        const lb = (fitValues[currentLine] || {}).lb;
        const scaleBox = $("[data-fit-scale]");
        if (scaleBox) {
          const scale = scaleOf(line);
          const thumb = $("[data-fit-slider]", scaleBox);
          scaleBox.classList.toggle("is-set", lb > 0);
          if (thumb && scale) {
            if (!slider) thumb.value = lb > 0 ? Math.min(scale.max, Math.max(scale.min, Math.round(lb))) : scale.min;
            thumb.setAttribute("aria-valuetext", lb > 0 ? `${thumb.value} pounds${rec ? `, ${rec.row.label}` : ""}` : "Not set");
          }
          $$("[data-fit-band]", scaleBox).forEach((band) => band.classList.toggle("is-match", Boolean(rec) && band.dataset.fitBand === rec.row.key));
        }
        // the suggested size is counted, never the weight or measurements typed in
        if (rec) signal("fit_rec", `${currentLine}:${String(rec.row.key).toLowerCase()}`, ["quiz", "complete", "hub.fit.finder"], { once: true });
      }, slider ? 0 : 250);
    });
  }

  // ------------------------------------------------------------------ lightbox
  // While it is open everything else on the page is inert (focus can't leave, clicks don't land).
  const lightbox = $("[data-tk9sh-lightbox]");
  let lastFocus = null;
  const inerted = [];
  function setInert(on) {
    if (!lightbox) return;
    if (on) {
      let node = lightbox;
      while (node && node !== document.body && node.parentElement) {
        for (const sibling of node.parentElement.children) {
          if (sibling !== node && !sibling.hasAttribute("inert")) { sibling.setAttribute("inert", ""); inerted.push(sibling); }
        }
        node = node.parentElement;
      }
    } else {
      inerted.splice(0).forEach((el) => el.removeAttribute("inert"));
    }
  }
  function openLightbox(item, from) {
    if (!lightbox) return;
    lastFocus = from || document.activeElement;
    const media = $("[data-tk9sh-lightbox-media]", lightbox);
    media.innerHTML = item.type === "video"
      ? `<iframe src="https://www.youtube-nocookie.com/embed/${encodeURIComponent(item.id)}?autoplay=1&rel=0" title="${esc(item.title)}" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe>`
      : `<img src="${esc(item.src)}" alt="${esc(item.title)}">`;
    lightbox.setAttribute("aria-label", item.title);
    lightbox.hidden = false;
    setInert(true);
    document.documentElement.style.overflow = "hidden";
    $("[data-tk9sh-close]", lightbox).focus();
    signal("media", item.type === "video" ? "video" : "guide", item.type === "video" ? ["video", "play", "hub.fit.video"] : ["size_guide", "open", "hub.fit.guide"]);
  }
  function closeLightbox() {
    if (!lightbox || lightbox.hidden) return;
    lightbox.hidden = true;
    $("[data-tk9sh-lightbox-media]", lightbox).innerHTML = "";
    setInert(false);
    document.documentElement.style.overflow = "";
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }
  if (lightbox) {
    lightbox.addEventListener("click", (event) => {
      if (event.target === lightbox || event.target.closest("[data-tk9sh-close]")) closeLightbox();
    });
    // Fallback focus trap for browsers without `inert`.
    lightbox.addEventListener("keydown", (event) => {
      if (event.key !== "Tab") return;
      const focusable = $$("button, [href], iframe, [tabindex]:not([tabindex='-1'])", lightbox);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
  }
  document.addEventListener("keydown", (event) => { if (event.key === "Escape") closeLightbox(); });

  // ------------------------------------------------------------------ FAQ
  // Counted when it opens, however it was opened (tap, deep link, "See it with the other answers", search). Each answer
  // is a declared Watch component (hub.faq.<name>), so the recorder counts its opens itself: no Watch call here.
  $$(".tk9sh__faq details").forEach((item) => {
    item.addEventListener("toggle", () => { if (item.open) track("faq_open", item.id || "faq", { once: true }); });
  });
  function flash(item) {
    item.classList.add("is-flash");
    setTimeout(() => item.classList.remove("is-flash"), 1600);
  }
  function openAnswer(item) {
    if (item.tagName === "DETAILS") item.open = true;
    scrollToEl(item);
    flash(item);
  }

  // ------------------------------------------------------------------ help library (collapses on phones)
  const decks = $$("[data-tk9sh-deck]");
  const phone = window.matchMedia("(max-width: 900px)");
  function enhanceDecks() {
    decks.forEach((deck, i) => {
      const title = $(".tk9sh__deck-title", deck);
      const links = $(".tk9sh__deck-links", deck);
      if (!title || !links) return;
      links.id = links.id || `tk9sh-deck-${i}`;
      const toggle = $(".tk9sh__deck-toggle", title);
      if (phone.matches && !toggle) {
        const name = $(".tk9sh__deck-name", title);
        const button = document.createElement("button");
        button.type = "button";
        button.className = "tk9sh__deck-toggle";
        button.setAttribute("aria-expanded", "false");
        button.setAttribute("aria-controls", links.id);
        button.innerHTML = `<span class="tk9sh__deck-name">${name.innerHTML}</span>${ICON_CHEVRON}`;
        name.replaceWith(button);
        links.hidden = true;
      } else if (!phone.matches && toggle) {
        const span = document.createElement("span");
        span.className = "tk9sh__deck-name";
        span.innerHTML = $(".tk9sh__deck-name", toggle).innerHTML;
        toggle.replaceWith(span);
        links.hidden = false;
      }
    });
  }
  document.addEventListener("click", (event) => {
    const toggle = event.target.closest(".tk9sh__deck-toggle");
    if (!toggle) return;
    const open = toggle.getAttribute("aria-expanded") !== "true";
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    const links = document.getElementById(toggle.getAttribute("aria-controls"));
    if (links) links.hidden = !open;
    if (open) {
      // deck ids come from the declared Watch component (hub.library.fit / .walks / .policies)
      const deck = toggle.closest("[data-tk9sh-deck]");
      const key = slug(((deck && deck.dataset.watchComponent) || "").split(".").pop(), 24) || "deck";
      signal("deck", key, ["cta", "open", `hub.library.${key}`], { once: true });
    }
  });
  enhanceDecks();
  if (phone.addEventListener) phone.addEventListener("change", enhanceDecks);

  // ------------------------------------------------------------------ search
  // Small on-page engine: every [data-tk9sh-index] element is a document with a title, extra
  // keywords (what people actually type) and, for answers, its text. Words are stemmed, rare words
  // count for more, near-misses and typos are forgiven, and weak matches are dropped — an
  // out-of-scope question gets the "ask our team" state instead of noise.
  const search$ = $(".tk9sh__search");
  const input = $("[data-tk9sh-search]");
  const popup = $("[data-tk9sh-popup]");
  const results = $("[data-tk9sh-results]");
  const empty = $("[data-tk9sh-empty]");
  const status = $("[data-tk9sh-status]");
  const clearBtn = $("[data-tk9sh-clear]");
  const kbd = $("[data-tk9sh-kbd]");
  const answerBox = $("[data-tk9sh-answer]");

  const KIND_LABEL = { faq: "Answer", path: "Do it now", fit: "Size and fit", link: "Guide", contact: "Contact", promise: "Promise" };
  const KIND_BOOST = { path: 1, faq: 1, contact: 1, fit: 1, link: 1, promise: 0.85 };
  const KIND_ORDER = { path: 0, faq: 1, contact: 2, fit: 3, link: 4, promise: 5 };
  const FIELD_WEIGHT = { title: 3, keys: 2.8, body: 1 };
  const STOP = new Set("a about all also am an and any are as at be because been but by can could did do does doing for from get got had has have having how i if in into is it its just me my need needs of on or our so some than that the their them then there these they this to too up us want was we were what when where which who why will with would you your please hi hello help something anything someone somebody everything thing things stuff really very much many may might should shall must long take takes time dog dogs see thanks thank thx".split(" "));

  const fold = (value) => String(value == null ? "" : value).toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "").replace(/['’]/g, "").replace(/[^a-z0-9]+/g, " ").trim();
  function stem(word) {
    let w = word;
    if (w.length <= 3) return w;
    if (w.endsWith("ies") && w.length > 4) w = `${w.slice(0, -3)}y`;
    else if (w.endsWith("sses")) w = w.slice(0, -2);
    else if (w.endsWith("s") && !w.endsWith("ss") && !w.endsWith("us")) w = w.slice(0, -1);
    if (w.length > 5 && w.endsWith("ing")) w = w.slice(0, -3);
    else if (w.length > 4 && w.endsWith("ed")) w = w.slice(0, -2);
    if (/([b-df-hj-np-tv-z])\1$/.test(w)) w = w.slice(0, -1);
    if (w.length > 3 && w.endsWith("e")) w = w.slice(0, -1);
    return w;
  }
  const words = (text) => fold(text).split(" ").filter((t) => t.length > 1 && !STOP.has(t));
  const stems = (text) => words(text).map(stem);
  const clip = (text, n = 110) => { const t = String(text || "").replace(/\s+/g, " ").trim(); return t.length > n ? `${t.slice(0, n).trim()}…` : t; };

  // Optimal-string-alignment distance (a transposition counts as one edit).
  function osa(a, b) {
    const m = a.length;
    const n = b.length;
    const d = Array.from({ length: m + 1 }, (_, i) => { const row = new Array(n + 1).fill(0); row[0] = i; return row; });
    for (let j = 1; j <= n; j++) d[0][j] = j;
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
    return d[m][n];
  }

  const entries = $$("[data-tk9sh-index]").map((el) => {
    const kind = el.dataset.tk9shKind || (el.tagName === "DETAILS" ? "faq" : el.classList.contains("tk9sh__path") ? "path" : el.hasAttribute("data-fit-tab") ? "fit" : "link");
    const title = el.dataset.tk9shIndex;
    let body = "";
    let snippet = "";
    if (kind === "faq") { body = ($("div > p", el) || {}).textContent || ""; snippet = clip(body); }
    else if (kind === "promise") { body = ($(".tk9sh__promise-text span", el) || {}).textContent || ""; snippet = clip(body); }
    else if (kind === "contact") { snippet = $$(".tk9sh__contact-facts li", el).map((li) => li.textContent.trim()).join(" · "); }
    else if (kind === "fit") { snippet = "Size chart, videos & guides"; }
    else { snippet = (($("small", el) || {}).textContent || "").trim(); }
    const f = { title: new Set(stems(title)), keys: new Set(stems(el.dataset.tk9shKeywords || "")), body: new Set(stems(body)) };
    return { el, kind, title, snippet, body, f, popular: el.hasAttribute("data-tk9sh-popular") };
  });
  const df = new Map();
  entries.forEach((entry) => {
    new Set([...entry.f.title, ...entry.f.keys, ...entry.f.body]).forEach((s) => df.set(s, (df.get(s) || 0) + 1));
  });
  const vocab = [...df.keys()];
  const N = Math.max(entries.length, 1);
  const idfOf = (s) => Math.log(1 + N / (1 + (df.get(s) || 0)));

  // One query word → the vocabulary stems it can mean (exact, prefix/as-you-type, then typo).
  function resolve(token, isLast) {
    const s = stem(token);
    if (df.has(s)) return [{ stem: s, q: 1 }];
    const out = [];
    if (s.length >= 4 || (isLast && s.length >= 3)) {
      for (const v of vocab) if (v.startsWith(s) || (v.length >= 4 && s.startsWith(v))) out.push({ stem: v, q: 0.85 });
    }
    if (out.length) return out;
    if (s.length >= 5) {
      const max = s.length >= 9 ? 2 : 1;
      for (const v of vocab) if (v.length >= 5 && v[0] === s[0] && Math.abs(v.length - s.length) <= max && osa(s, v) <= max) out.push({ stem: v, q: 0.65 });
    }
    return out;
  }
  function search(query) {
    const tokens = fold(query).split(" ").filter((t) => t && !STOP.has(t));
    if (!tokens.length) return [];
    const resolved = tokens.map((token, i) => ({ token, res: resolve(token, i === tokens.length - 1) }));
    const terms = resolved
      .filter((t) => t.res.length)
      .map((t) => ({ ...t, idf: Math.max(...t.res.map((r) => idfOf(r.stem))) }));
    if (!terms.length) return [];
    // A word this page has never seen ("wholesale", "canada") often means the question is about something else. A
    // match that rests only on an answer's body text is not shown for such a question; title and keyword matches are.
    const hasUnknown = resolved.some((t) => !t.res.length && t.token.length > 2);
    const totalIdf = terms.reduce((sum, t) => sum + t.idf, 0);
    const unknownIdf = resolved.filter((t) => !t.res.length && t.token.length > 2).length * Math.max(...terms.map((t) => t.idf));
    const scored = [];
    for (const entry of entries) {
      let got = 0;
      let score = 0;
      let strong = false;
      const matched = new Set();
      const inTitle = new Set();
      for (const term of terms) {
        let best = 0;
        for (const r of term.res) {
          const weight = entry.f.title.has(r.stem) ? FIELD_WEIGHT.title : entry.f.keys.has(r.stem) ? FIELD_WEIGHT.keys : entry.f.body.has(r.stem) ? FIELD_WEIGHT.body : 0;
          if (weight && weight * r.q > best) best = weight * r.q;
          if (weight > FIELD_WEIGHT.body) strong = true;
          if (weight && r.q >= 0.85) matched.add(r.stem);
          if (weight === FIELD_WEIGHT.title && r.q >= 0.85) inTitle.add(r.stem);
        }
        if (best > 0) { got += term.idf; score += term.idf * term.idf * best; }
      }
      const coverage = got / totalIdf;
      if (coverage < 0.3 || (hasUnknown && !strong)) continue;
      // Between two entries that match equally, the one whose title is mostly the question wins ("where is my
      // refund" → "Where's my refund?" rather than the longer returns answer).
      const specificity = entry.f.title.size ? inTitle.size / entry.f.title.size : 0;
      scored.push({ entry, matched, score: score * (0.4 + 0.6 * coverage) * KIND_BOOST[entry.kind] * (1 + 0.12 * specificity), coverage, confidence: got / (totalIdf + unknownIdf) });
    }
    scored.sort((a, b) => b.score - a.score || KIND_ORDER[a.entry.kind] - KIND_ORDER[b.entry.kind]);
    if (!scored.length) return [];
    const floor = scored[0].score * 0.3;
    const maxPossible = terms.reduce((sum, t) => sum + t.idf * t.idf, 0) * FIELD_WEIGHT.title;
    return scored.filter((hit) => hit.score >= floor && hit.score >= maxPossible * 0.12).slice(0, 6);
  }
  function highlight(title, matched) {
    if (!matched || !matched.size) return esc(title);
    return String(title).split(/([A-Za-z0-9’']+)/).map((part, i) => (i % 2 === 1 && matched.has(stem(fold(part))) ? `<mark>${esc(part)}</mark>` : esc(part))).join("");
  }

  let hits = [];
  let active = -1;
  let popularShown = false;
  let answerId = "";
  let prefilled = false;

  // A search is counted once it has settled (a pause in typing, Enter, or picking a result), not per keystroke.
  // Counted: how many results and the kind of the top one. The words themselves leave the page only when the
  // search found nothing, analytics are accepted, and they are plain words (see plainWords).
  let pendingSearch = null;
  let searchTimer = null;
  let lastSearch = "";
  const plainWords = (raw, folded) => !/@|:\/\/|www\./i.test(raw) && /^[a-z]+( [a-z]+){0,5}$/.test(folded) && folded.length >= 3 && folded.length <= 60;
  function queueSearch(query, count, topKind) {
    clearTimeout(searchTimer);
    const folded = fold(query);
    pendingSearch = folded.length > 1 ? { folded, raw: query, count, topKind } : null;
    if (pendingSearch) searchTimer = setTimeout(logSearch, 1000);
  }
  function logSearch() {
    clearTimeout(searchTimer);
    const s = pendingSearch;
    pendingSearch = null;
    if (!s || s.folded === lastSearch) return;
    lastSearch = s.folded;
    signal("search", Math.min(s.count, 6), ["quiz", "change", s.count ? "hub.search.results" : "hub.search.none"]);
    track("search_result", s.count ? s.topKind : "none");
    if (!s.count && consent() === true && plainWords(s.raw, s.folded)) track("search_none_q", s.folded, { once: true });
  }
  // A stable id for whatever a search result points at (never its visible text, except for plain library links).
  const entryId = (entry) => slug(entry.el.dataset.fitTab || entry.el.dataset.tk9shIntent || entry.el.dataset.tk9shJump || entry.el.id || entry.title);
  const say = (text) => { if (status) status.textContent = text; };
  const popupOpen = () => popup && !popup.hidden;
  function openPopup() { popup.hidden = false; input.setAttribute("aria-expanded", "true"); }
  function closePopup() {
    if (!popup) return;
    popup.hidden = true;
    active = -1;
    input.setAttribute("aria-expanded", "false");
    input.removeAttribute("aria-activedescendant");
  }
  const optionHtml = (hit, i) => `<div class="tk9sh__result" role="option" id="tk9sh-r${i}" data-hit="${i}" aria-selected="false"><span class="tk9sh__result-top"><b>${highlight(hit.entry.title, hit.matched)}</b><span class="tk9sh__result-tag">${KIND_LABEL[hit.entry.kind]}</span></span>${hit.entry.snippet ? `<small>${esc(hit.entry.snippet)}</small>` : ""}</div>`;
  function syncChrome() {
    const has = Boolean(input.value);
    if (clearBtn) clearBtn.hidden = !has;
    if (kbd) kbd.hidden = has;
  }
  function showPopular() {
    const popular = entries.filter((e) => e.popular);
    if (!popular.length) { closePopup(); return; }
    hits = popular.map((entry) => ({ entry, matched: null }));
    popularShown = true;
    active = -1;
    empty.hidden = true;
    results.innerHTML = `<div role="group" aria-labelledby="tk9sh-pop-label"><div class="tk9sh__group-label" id="tk9sh-pop-label" role="presentation">Popular answers</div>${hits.map(optionHtml).join("")}</div>`;
    openPopup();
    say(`${hits.length} popular answers. Use the up and down arrows, then Enter.`);
  }
  function showResults(query) {
    syncChrome();
    if (!query.trim()) {
      // a cleared box is not a finished search
      clearTimeout(searchTimer);
      pendingSearch = null;
      if (document.activeElement === input) showPopular(); else closePopup();
      return;
    }
    hits = search(query);
    popularShown = false;
    active = -1;
    input.removeAttribute("aria-activedescendant");
    if (!hits.length) {
      results.innerHTML = "";
      empty.hidden = false;
      openPopup();
      say("No quick answer for that. Press Enter to ask our team.");
      queueSearch(query, 0, "none");
      return;
    }
    empty.hidden = true;
    results.innerHTML = hits.map(optionHtml).join("");
    openPopup();
    say(`${hits.length} ${hits.length === 1 ? "result" : "results"}. Use the up and down arrows, then Enter.`);
    queueSearch(query, hits.length, hits[0].entry.kind);
  }
  function setActive(next) {
    const options = $$("[data-hit]", results);
    if (!options.length) return;
    active = (next + options.length) % options.length;
    options.forEach((el, i) => { el.classList.toggle("is-active", i === active); el.setAttribute("aria-selected", i === active ? "true" : "false"); });
    input.setAttribute("aria-activedescendant", options[active].id);
    options[active].scrollIntoView({ block: "nearest" });
  }

  // Inline answers: FAQ and promise results open right under the search box instead of sending
  // the reader to the bottom of a long page.
  function revealEntry(el) {
    if (answerBox) { answerBox.hidden = true; answerBox.innerHTML = ""; }
    openAnswer(el);
  }
  function showAnswer(entry) {
    if (!answerBox) return openAnswer(entry.el);
    const el = entry.el;
    const source = entry.kind === "faq" ? $("div", el) : $(".tk9sh__promise-text span", el);
    answerBox.innerHTML = `
      <div class="tk9sh__answer-card" role="region" aria-labelledby="tk9sh-answer-title" tabindex="-1">
        <button type="button" class="tk9sh__answer-close" aria-label="Close answer" data-tk9sh-answer-close>${ICON_X}</button>
        <span class="tk9sh__kicker">Quick answer</span>
        <h2 class="tk9sh__answer-title" id="tk9sh-answer-title">${esc(entry.title)}</h2>
        <div class="tk9sh__answer-body" data-tk9sh-answer-body></div>
        <p class="tk9sh__answer-foot">Not what you need? <button type="button" data-ask-plain>Ask our team</button>${el.id ? ` · <a href="#${esc(el.id)}" data-tk9sh-reveal="${esc(el.id)}">See it with the other answers</a>` : ""}</p>
      </div>`;
    const body = $("[data-tk9sh-answer-body]", answerBox);
    if (entry.kind === "faq") Array.from(source.childNodes).forEach((node) => body.appendChild(node.cloneNode(true)));
    else { const p = document.createElement("p"); p.textContent = source.textContent; body.appendChild(p); }
    answerBox.hidden = false;
    const card = $(".tk9sh__answer-card", answerBox);
    card.focus({ preventScroll: true });
    card.scrollIntoView({ block: "nearest", behavior: reduceMotion ? "auto" : "smooth" });
    answerId = el.id || slug(entry.title);
    signal("answer_open", answerId, entry.kind === "promise"
      ? ["guarantee", "open", `hub.promise.${slug(answerId.replace(/^promise-/, ""), 30)}`]
      : ["faq", "open", `hub.faq.${slug(answerId.replace(/^faq-/, ""), 30)}`], { once: true });
  }
  function closeAnswer({ focusInput = true } = {}) {
    if (!answerBox || answerBox.hidden) return;
    answerBox.hidden = true;
    answerBox.innerHTML = "";
    if (focusInput && input) input.focus({ preventScroll: true });
  }
  // from: "empty" (the no-results state), "answer" (an answer card didn't do it), "search" (the Contact result was
  // picked) or "chat" (the chat helper passes the customer's own words as `text`)
  function ask({ prefill = false, from = "search", text = "" } = {}) {
    closePopup();
    logSearch();
    const body = $("[data-tk9sh-body]");
    const words = text || (prefill && input ? input.value.trim() : "");
    if (body && words && !body.value) { body.value = words; prefilled = true; }
    jump("contact");
    if (body) setTimeout(() => body.focus({ preventScroll: true }), reduceMotion ? 0 : 450);
    signal("contact_ask", from, ["cta", "open", "hub.contact.ask"]);
  }
  function choose(hit) {
    if (!hit) return;
    closePopup();
    logSearch();
    const { entry } = hit;
    const pick = popularShown ? "popular_pick" : "search_pick";
    signal(pick, popularShown ? entryId(entry) : `${entry.kind}:${entryId(entry)}`, ["quiz", "complete", "hub.search.pick"]);
    if (entry.kind === "faq" || entry.kind === "promise") return showAnswer(entry);
    if (entry.kind === "fit") return selectLine(entry.el.dataset.fitTab, { scroll: true });
    if (entry.kind === "contact") return ask({ from: "search" });
    intentSource = "search";
    try { entry.el.click(); } finally { intentSource = ""; }
  }

  if (input && popup && results) {
    let debounce = null;
    input.addEventListener("input", () => { syncChrome(); clearTimeout(debounce); debounce = setTimeout(() => showResults(input.value), 90); });
    input.addEventListener("focus", () => { if (input.value.trim()) showResults(input.value); else showPopular(); });
    input.addEventListener("blur", () => setTimeout(() => { if (search$ && !search$.contains(document.activeElement)) closePopup(); }, 120));
    input.addEventListener("keydown", (event) => {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        if (!popupOpen()) { showResults(input.value); return; }
        setActive(active + (event.key === "ArrowDown" ? 1 : -1));
      } else if (event.key === "Home" || event.key === "End") {
        if (popupOpen() && active > -1) { event.preventDefault(); setActive(event.key === "Home" ? 0 : hits.length - 1); }
      } else if (event.key === "Enter") {
        event.preventDefault();
        if (popupOpen() && hits.length && (active > -1 || input.value.trim())) choose(hits[Math.max(active, 0)]);
        else if (input.value.trim()) ask({ prefill: true, from: "empty" });
      } else if (event.key === "Escape") {
        // preventDefault: a type=search box clears itself on Escape in Chrome/Safari (and fires "input", which would
        // reopen the list). First Escape closes the list, the second clears the box.
        if (popupOpen()) { event.preventDefault(); event.stopPropagation(); closePopup(); }
        else if (input.value) { event.preventDefault(); input.value = ""; syncChrome(); }
      }
    });
    results.addEventListener("mousedown", (event) => event.preventDefault());
    results.addEventListener("click", (event) => {
      const option = event.target.closest("[data-hit]");
      if (option) choose(hits[Number(option.dataset.hit)]);
    });
    popup.addEventListener("mousedown", (event) => { if (event.target.closest("[data-ask]")) event.preventDefault(); });
    popup.addEventListener("click", (event) => { if (event.target.closest("[data-ask]")) ask({ prefill: true, from: "empty" }); });
    if (clearBtn) {
      clearBtn.addEventListener("click", () => { input.value = ""; syncChrome(); input.focus(); showPopular(); });
    }
    document.addEventListener("click", (event) => { if (!event.target.closest(".tk9sh__search")) closePopup(); });
    // "/" jumps to the search box from anywhere on the page (like most help centers).
    document.addEventListener("keydown", (event) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey || isTyping(event.target) || (lightbox && !lightbox.hidden)) return;
      event.preventDefault();
      input.focus();
      input.select();
      input.scrollIntoView({ block: "center", behavior: reduceMotion ? "auto" : "smooth" });
    });
  }
  if (answerBox) {
    answerBox.addEventListener("click", (event) => {
      if (event.target.closest("[data-tk9sh-answer-close]")) return closeAnswer();
      // "Not what you need? Ask our team" on an answer card: the answer didn't settle it
      if (event.target.closest("[data-ask-plain]")) { if (answerId) track("answer_ask", answerId); return ask({ from: "answer" }); }
      const reveal = event.target.closest("[data-tk9sh-reveal]");
      if (reveal) { event.preventDefault(); const target = document.getElementById(reveal.dataset.tk9shReveal); if (target) revealEntry(target); }
    });
    answerBox.addEventListener("keydown", (event) => { if (event.key === "Escape") { event.stopPropagation(); closeAnswer(); } });
  }

  // ------------------------------------------------------------------ contact
  const form = $("[data-tk9sh-contact]");
  const submit = form && $("[data-tk9sh-submit]", form);
  if (form) {
    // a field gets focus (not the confirmation message that is focused after a send)
    form.addEventListener("focusin", (event) => { if (event.target.matches("input, textarea, select")) track("contact_start", "", { once: true }); });
    form.addEventListener("submit", () => {
      // "prefilled": the message started as the words typed in the search box
      signal("contact_submit", prefilled ? "prefilled" : "", ["cta", "complete", "hub.contact"]);
      flush();
      if (submit) setTimeout(() => { submit.disabled = true; submit.setAttribute("aria-busy", "true"); submit.textContent = "Sending…"; }, 0);
    });
    // Back/forward cache can restore the page mid-send; make the button usable again.
    window.addEventListener("pageshow", (event) => {
      if (event.persisted && submit) { submit.disabled = false; submit.removeAttribute("aria-busy"); submit.textContent = "Send message"; }
    });
  }
  // The page that comes back after a message went through (the form's own confirmation is on screen).
  const posted = $("[data-tk9sh-posted]");
  if (posted) { track("contact_posted", "", { once: true }); jump("contact"); posted.focus({ preventScroll: true }); }

  // ------------------------------------------------------------------ deep links
  function applyDeepLink(initial) {
    const params = new URLSearchParams(location.search);
    const hash = decodeURIComponent(location.hash.slice(1)).toLowerCase();
    const delay = initial ? 350 : 0;
    const flow = FLOW_ALIASES[(params.get("intent") || params.get("flow") || hash).toLowerCase()];
    if (flow) { setTimeout(() => { openIntent(flow); handOff(flow, "deeplink"); }, delay); return; }
    if (hash.startsWith("faq-")) {
      const item = document.getElementById(hash);
      if (item && item.tagName === "DETAILS") setTimeout(() => openAnswer(item), delay);
      return;
    }
    const fit = hash.startsWith("fit-") ? hash.slice(4) : params.get("fit");
    if (fit && (LINES[fit] || fit === "seat")) { setTimeout(() => selectLine(fit, { scroll: true }), delay); return; }
    if (hash === "fit") setTimeout(() => jump("fit"), delay);
  }
  applyDeepLink(true);
  window.addEventListener("hashchange", () => applyDeepLink(false));
  const startQuery = new URLSearchParams(location.search).get("q");
  if (startQuery && input && popup) { input.value = startQuery.slice(0, 120); showResults(input.value); }
  syncChrome();

  // ------------------------------------------------------------------ analytics: the page itself
  // How the visitor got here (no URLs or referrers are recorded): a hand-off link, a search link, a fit link, an FAQ
  // link, any other #anchor, a link from another site, or none of those.
  function arrival() {
    const params = new URLSearchParams(location.search);
    const hash = decodeURIComponent(location.hash.slice(1)).toLowerCase();
    if (FLOW_ALIASES[(params.get("intent") || params.get("flow") || hash).toLowerCase()]) return "intent";
    if (params.get("q")) return "search";
    if (params.get("fit") || hash === "fit" || hash.startsWith("fit-")) return "fit";
    if (hash.startsWith("faq-")) return "faq";
    if (hash) return "hash";
    try { if (document.referrer && new URL(document.referrer).origin !== location.origin) return "other"; } catch {}
    return "direct";
  }
  track("view", arrival());

  const notice = $(".tk9sh__notice", top);
  if (notice) {
    track("notice_view", /tk9sh__notice--warning/.test(notice.className) ? "warning" : "info", { once: true });
    notice.addEventListener("click", (event) => { if (event.target.closest("a")) track("notice_click", "", { once: true }); });
  }

  // How far down the visitor gets: the furthest section (in page order) that had about a third of it, or 40% of the
  // screen, in view. One event per visit, sent when the visitor leaves.
  const DEPTH_ORDER = ["top", "paths", "order-help", "fit", "library", "promises", "faq", "contact"];
  let deepest = -1;
  let depthSent = false;
  const orderHelp = portal();
  if (orderHelp && !orderHelp.dataset.tk9shSection) orderHelp.dataset.tk9shSection = "order-help";
  if ("IntersectionObserver" in window) {
    const seenSections = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const index = DEPTH_ORDER.indexOf(entry.target.dataset.tk9shSection);
        if (index <= deepest || !entry.isIntersecting) return;
        if (entry.intersectionRatio >= 0.35 || entry.intersectionRect.height >= window.innerHeight * 0.4) deepest = index;
      });
    }, { threshold: [0, 0.02, 0.05, 0.1, 0.2, 0.35, 0.5] });
    $$("[data-tk9sh-section]").forEach((el) => seenSections.observe(el));
  }

  // ------------------------------------------------------------------ chat helper bridge
  // The chat helper (tk9-support-chat.js) answers only from this page's own index and reuses its hand-offs, so it can
  // never say something the page does not. It gets read access to the index and these few actions, nothing else.
  const describe = (entry) => ({
    kind: entry.kind,
    id: entryId(entry),
    title: entry.title,
    flow: entry.el.dataset.tk9shIntent || "",
    jump: entry.el.dataset.tk9shJump || "",
    line: entry.el.dataset.fitTab || "",
    href: entry.kind === "link" ? entry.el.getAttribute("href") || "" : "",
    entry
  });
  window.TK9SupportHub = {
    enabled: top.dataset.chat !== "false",
    version: PAGE_VERSION,
    reduceMotion,
    reporting,
    consent,
    deviceClass,
    post,
    watch,
    search: (query) => search(query).map((hit) => ({ ...describe(hit.entry), confidence: hit.confidence })),
    // What this page can show, for the helper's reading step: ids, names and the first words of each item.
    options() {
      const seen = new Set();
      // pages that only list other pages (the FAQ, the guides index, the blog) are never an answer, so they are left out
      return entries.filter((entry) => !(entry.kind === "link" && /^\/(pages\/(faq|guides)|blogs\/[^/]+)\/?$/.test(entry.el.getAttribute("href") || "")))
        .map((entry) => ({ id: entryId(entry), kind: entry.kind, title: entry.title, about: clip(entry.body || entry.snippet, 220) }))
        .filter((option) => option.id && !seen.has(option.id) && seen.add(option.id));
    },
    // The reading step (Order Help backend, action chat_pick): a model reads the typed question against options() and
    // names the item that answers it, a few that might, or none. It only chooses; the words shown are this page's own.
    // Resolves to { best, maybe, vague } built from this index, or null when the step is off, slow or failed, and the
    // helper then falls back to matching words. The question is already scrubbed by the helper. Off unless the
    // section setting "Quick help reads typed questions" is on; never runs in the theme editor or the portal's demo
    // mode; on localhost only with ?tk9sh_pick=1.
    picking() {
      const tryout = /[?&]tk9sh_pick=1(&|$)/.test(location.search);
      if (!/^https:\/\//.test(API) || DEMO || (window.Shopify && window.Shopify.designMode)) return false;
      return DEV_HOST ? tryout : top.dataset.chatPick === "true";
    },
    async pick(question, ms = 6000) {
      if (!this.picking()) return null;
      const control = typeof AbortController === "function" ? new AbortController() : null;
      const timer = setTimeout(() => { if (control) control.abort(); }, ms);
      try {
        const response = await fetch(API, { method: "POST", body: JSON.stringify({ action: "chat_pick", q: String(question || "").slice(0, 300), options: this.options() }), headers: { "content-type": "text/plain" }, credentials: "omit", mode: "cors", signal: control ? control.signal : undefined });
        const pick = response.ok ? (await response.json()).pick : null;
        if (!pick) return null;
        const item = (id) => { const entry = entries.find((candidate) => entryId(candidate) === id); return entry ? describe(entry) : null; };
        return { best: pick.best ? item(pick.best) : null, maybe: (Array.isArray(pick.maybe) ? pick.maybe : []).map(item).filter(Boolean), vague: pick.vague === true };
      } catch {
        return null;
      } finally {
        clearTimeout(timer);
      }
    },
    // The live chat on the support engine (Order Help backend, action chat_engine): the engine that drafts the team's
    // Zendesk replies answers a typed question, with the customer's order once the chat's order form has looked it up,
    // and names the Order Help task that does the rest. It never executes anything. Resolves to { reply, buttons,
    // verification } or null when it is off, slow or unreachable, and the helper then answers from this page. Off
    // unless the section setting "Quick help answers with the support engine" is on; never in the theme editor or
    // demo mode; on localhost only with ?tk9sh_engine=1.
    engineChat() {
      const tryout = /[?&]tk9sh_engine=1(&|$)/.test(location.search);
      if (!/^https?:\/\//.test(API) || DEMO || (window.Shopify && window.Shopify.designMode)) return false;
      if (DEV_HOST) return tryout;
      return /^https:\/\//.test(API) && top.dataset.chatEngine === "true";
    },
    async engine({ sid, message, verification }, ms = 50000) {
      if (!this.engineChat()) return null;
      const control = typeof AbortController === "function" ? new AbortController() : null;
      const timer = setTimeout(() => { if (control) control.abort(); }, ms);
      try {
        const response = await fetch(API, { method: "POST", body: JSON.stringify({ action: "chat_engine", sid, message: String(message || "").slice(0, 600), ...(verification ? { verification } : {}) }), headers: { "content-type": "text/plain" }, credentials: "omit", mode: "cors", signal: control ? control.signal : undefined });
        const chat = response.ok ? (await response.json()).chat : null;
        return chat && typeof chat.reply === "string" ? chat : null;
      } catch {
        return null;
      } finally {
        clearTimeout(timer);
      }
    },
    paths: () => entries.filter((entry) => entry.kind === "path").map(describe),
    // the reviewed answer itself, as a copy of the page's own nodes
    answer(item) {
      const fragment = document.createDocumentFragment();
      const source = item.kind === "faq" ? $("div", item.entry.el) : $(".tk9sh__promise-text span", item.entry.el);
      if (!source) return fragment;
      if (item.kind === "faq") Array.from(source.childNodes).forEach((node) => fragment.appendChild(node.cloneNode(true)));
      else { const p = document.createElement("p"); p.textContent = source.textContent; fragment.appendChild(p); }
      return fragment;
    },
    // a hand-off, exactly as the page's own tiles do it
    open(item) {
      if (item.kind === "fit") return selectLine(item.line, { scroll: true });
      if (item.jump) return jump(item.jump);
      if (item.flow) { handOff(item.flow, "chat"); openIntent(item.flow); }
    },
    // true when the contact form's message box is empty, i.e. the customer's question will be filled in
    contactEmpty: () => { const body = $("[data-tk9sh-body]"); return Boolean(body && !body.value); },
    askTeam: (text) => ask({ from: "chat", text: String(text || "").slice(0, 600) })
  };
  try { document.dispatchEvent(new CustomEvent("tk9sh:ready")); } catch {}

  // Send what is queued when the visitor leaves or switches tabs (a settled search, depth and page health go with it).
  const leaving = () => {
    logSearch();
    if (!depthSent && deepest > -1) { depthSent = true; track("depth", DEPTH_ORDER[deepest]); }
    reportVitals();
    flush();
  };
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") leaving(); });
  window.addEventListener("pagehide", leaving);
})();
